// Unpublished Work © 2023-2024 Deere & Company.

import { GridSortModel } from "@mui/x-data-grid-pro";
import { AxiosError } from "axios";
import { Dayjs } from "dayjs";
import {
  LineItem,
  Order as OldOrder,
  ReviewLineItem,
  SavedAddress,
  ValidationResponse,
} from "../../api/orders/orderInterfaces";
import { IPromotion } from "../../types";
import { PartPackageDimensions } from "../parts/PartInfo/types";
import { PriceModalProps } from "./components/PriceModal/PriceModal";
import { LineServiceLevelSummary } from "./components/ServiceLevelSummary/components/ServiceLevels";
import { LineSummaryFilter } from "./Summary/components/LineSummary/types";

export enum OrderServiceType {
  PICKUP = "PICKUP",
  DELIVERY = "DELIVERY",
}

export enum LineState {
  CREATED = "CREATED",
  ENTERED = "ENTERED",
  VALIDATED = "VALIDATED",
  REVIEWED = "REVIEWED",
  DELETED = "DELETED",
  SUBMITTED = "SUBMITTED",
  EDITED = "EDITED",
}

export enum OrderType {
  EMERGENCY_ORDER = "EMERGENCY_ORDER",
  FUTURE_SHIPMENT = "FUTURE_SHIPMENT",
  PROMOTION_ORDER = "PROMOTION_ORDER",
  STOCK_ORDER = "STOCK_ORDER",
}

interface Promotion {
  id: string;
  description: string;
}

interface CancellationDetail {
  isRequestedForCancellation: boolean;
  isCancellationRejected: boolean;
}

export interface OrderAudit {
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export enum OrderStatus {
  UNSUBMITTED = "UNSUBMITTED",
  SUBMITTED = "SUBMITTED",
  CANCELLED = "CANCELLED",
  COMPLETED = "COMPLETED",
  ON_HOLD = "ON_HOLD",
  IN_PROCESS = "IN_PROCESS",
}

export enum LineStatus {
  CONFIRMED = "CONFIRMED",
  BACK_ORDERED = "BACK_ORDERED",
  SENT_TO_SUPPLIER = "SENT_TO_SUPPLIER",
  DEERE_INVESTIGATION = "DEERE_INVESTIGATION",
  IN_PROCESS = "IN_PROCESS",
  DEALER_INVESTIGATION = "DEALER_INVESTIGATION",
  USER_CANCELLED = "USER_CANCELLED",
  AUTO_CANCELLED = "AUTO_CANCELLED",
  SHIPPED = "SHIPPED",
  ON_HOLD = "ON_HOLD",
  PARTIALLY_SHIPPED = "PARTIALLY_SHIPPED",
  CANCELLED = "CANCELLED",
  ORDERED = "ORDERED",
  SENT_TO_FACTORY = "SENT_TO_FACTORY",
  ROUTED = "ROUTED",
  EXPORT_RESHIP = "EXPORT_RESHIP",
  ROUTED_TO_DEALER = "ROUTED_TO_DEALER",
  SENT_TO_DEALER = "SENT_TO_DEALER",
  SHIPPED_BY_DEALER = "SHIPPED_BY_DEALER",
}

export enum OrderClient {
  UI = "UI",
  PRISM = "PRISM",
  PMLINK = "PMLINK",
  SYSTEM = "SYSTEM",
  JDPOINT = "JDPOINT",
  POLL = "POLL",
  UNKNOWN_PRISM = "UNKNOWN",
}

export enum M2NOOutboundTransactionStatus {
  IN_PROCESS = "IN_PROCESS",
  FAILURE = "FAILURE",
  SUCCESS = "SUCCESS",
}

export interface Order {
  dealerLocation: string;
  accountId: string;
  dealerCreditLimit?: number | null;
  orderNumber: string;
  countryCode?: string;
  currencyCode?: string | null;
  orderType: OrderType;
  client?: OrderClient;
  orderStatus: OrderStatus;
  orderServiceType: OrderServiceType;
  orderConsolidation: boolean;
  dealerReference?: string | null;
  promotion?: IPromotion;
  requestedShipMonth?: Dayjs;
  orderNotes?: string | null;
  submittedBy?: string;
  lastUpdatedBy?: string;
  submittedAt?: Date;
  externalOrderNumber?: string;
  generatedFrom?: string;
  minimumOrderValue?: number | null;
  // Backend-computed total order value (e.g. from OrderReviewProcessor.computeOrderValue()).
  // When present, this should be preferred over the client-side sub-line price sum,
  // since it correctly accounts for lines under investigation that have no sub-lines yet.
  orderValue?: number | null;
  lastUpdatedAt?: Date;
  lines: Line[];
  isShipToAddressOverridden?: boolean;
  isShipToAddressValid?: boolean;
  shipToAddress?: Address;
  soldToAddress?: Address;
  outboundAutoSubmit?: boolean;
  discount?: string | null;
  discountDescription?: string | null;
  inProcessAsync?: boolean;
}

export interface Line {
  lineNumber: number;
  externalLineNumber?: string;
  id: string;
  partNumber: string;
  quantity: number;
  state: LineState;
  unsuccessfulValidations?: ValidationResponse[];
  totalEffectivePrice?: number | string;
  lineStatus?: LineStatus;
  bin: string;
  serviceLevelAgreement?: ServiceLevelAgreement;
  newServiceLevelAgreement?: NewServiceLevelAgreement;
  serviceLevelAgreementDescription?: string | null;
  carrier?: { carrierValue?: string };
  promotion?: Promotion;
  subLines: SubLine[];
  partDescription?: string;
  partItemCategory?: string;
  isLineEligibleForM2NO?: boolean;
  externalOrderNumber?: string;
  m2noOutboundOrderStatus?: M2NOOutboundTransactionStatus;
  uniqueLineId?: string;
  engineSerialNumber?: string[];
}

export interface SubLine {
  lineNumber: string;
  partDescription: string;
  partNumber: string;
  partItemCategory?: string;
  quantity: number;
  shippingUnit: string | null;
  shippingUnitText?: string;
  lineStatus: LineStatus;
  lineStatusCodes?: string[];
  serviceLevelAgreement?: ServiceLevelAgreement;
  newServiceLevelAgreement?: NewServiceLevelAgreement;
  serviceLevelAgreementDescription?: string | null;
  carrier?: { carrierValue?: string };
  effectivePrice: number | string;
  promotion?: Promotion;
  promiseDate?: Date;
  shipDate?: Date;
  cancellationDetail?: CancellationDetail;
  deliveryNumber?: string;
  nextApplicableStatus?: LineStatus;
  unsuccessfulValidations?: ValidationResponse[];
  investigations?: ValidationResponse[];
  validationStatus?: LineStatus;
  externalOrderNumber?: string;
  isScheduleLineEligibleForM2NO?: boolean;
  vendorDirectPart?: boolean;
  m2noOutboundOrderStatus?: M2NOOutboundTransactionStatus;
  isSubstituted: boolean;
  shipDateTime?: Date | string;
}

export interface LinePromotion {
  id: string;
  description: string;
}

export interface Address {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  phoneNumber?: string;
  shipMode?: string;
}

export interface LineServiceLevel {
  partNumber: string;
  packageQuantity: number;
  quantity: number;
  shipUnit: string;
  slaOrCarrierValue: string;
  weight?: number;
  volume?: number;
  volumeUnit?: string;
  weightUnit?: string;
}

export interface OrderCreationResponse {
  orderNumber: string;
  accountId: string;
  countryCode: string;
  currencyCode?: string | null;
  orderType: string;
  orderStatus: string;
  orderServiceType: string;
  client: string;
  orderConsolidation: boolean;
  dealerReference: string;
  orderNotes: string;
  submittedBy: string;
  submittedAt: string;
  externalOrderNumber: string;
  minimumOrderValue?: number | null;
  lineItems: LineItem[];
  audit: OrderAudit;
  shipDate?: string;
  shipToAddress?: SavedAddress;
  soldToAddress?: SavedAddress;
}

export type OrderUpdateResponse = OrderCreationResponse;

export interface OrderReviewResponse2 {
  orderNumber: string;
  accountId: string;
  countryCode: string;
  currencyCode?: string | null;
  orderType: string;
  orderStatus: string;
  orderServiceType: string;
  client: string;
  orderConsolidation: boolean;
  dealerReference: string;
  orderNotes: string;
  submittedBy: string;
  submittedAt: string;
  externalOrderNumber: string;
  unsuccessfulHeaderValidations: ValidationResponse[];
  unsuccessfulOrderInvestigations: ValidationResponse[];
  orderValue: number;
  lineItemReviews: ReviewLineItem[];
  audit: OrderAudit;
}

export type OrderCancelResponse = OldOrder;

export interface PendingOrder {
  orderNumber: string;
  orderType: string;
  dealerReference: string | null;
  creditValue: number | null;
  submittedAt: Date | null;
}

export type PendingOrdersGroup = "OPEN_ORDERS" | "BLOCKED_ORDERS";

export interface ServiceLevelAgreement {
  partNumber: string;
  shippingUnit: string | null;
  currentValue?: string;
  applicableSLRList?: string[];
}

export interface NewServiceLevelAgreement {
  partNumber: string;
  shippingUnit: string | null;
  currentValue?: Map<string, string>;
  applicableSLRList?: Map<string, string>;
}

export interface OrderLineItem {
  lineNumber: number[];
  scheduleLineNumber?: string;
  externalParentLineNumber?: string;
  totalNonCancelledSubLines?: number;
  lineId: string;
  parentLineId: string;
  lineType: "parentLineItem" | "scheduledLines";
  quantity: number;
  state: LineState;
  parentQuantity: number;
  partNumber: string;
  parentPartNumber: string;
  partItemCategory?: string;
  description?: string;
  effectivePrice?: string | number;
  deliveryNumber?: string;
  shipUnit: string | null;
  shipUnitText?: string;
  bin?: string;
  externalOrderNumber?: string;
  m2noOutboundOrderStatus?: M2NOOutboundTransactionStatus;
  promotion?: {
    id: string;
    description: string;
  };
  unsuccessfulValidations?: ValidationResponse[];
  investigations?: ValidationResponse[];
  serviceLevelRequested?: ServiceLevelAgreement;
  serviceLevelAgreementDescription?: string | null;
  newServiceLevelRequested?: NewServiceLevelAgreement;
  carrier?: {
    carrierValue?: string;
  };
  warning: string;
  status: string;
  statusCodes?: string[];
  action: string;
  promiseDate?: string;
  shipDate?: string;
  shipDateTime?: Date | string;
  cancellationDetail?: CancellationDetail;
  nextApplicableStatus?: LineStatus;
  isLineEligibleForM2NO?: boolean;
  vendorDirectPart?: boolean;
  isSubstituted: boolean;
  uniqueLineId?: string;
  engineSerialNumber?: string[];
}

export interface BackOrdersDetailsResponse {
  stockQuantity: number;
  emergencyQuantity: number;
  promiseDatesQuantity: number;
  promiseDate: string;
}

export const DEFAULT_COUNTRY_CODE = "USD";
export const DEFAULT_COUNTRY_CURRENCY_SYMBOL = "$";
export const countryCurrencyMap = new Map([
  ["AR", "USD"],
  ["AT", "EUR"],
  ["AU", "AUD"],
  ["AX", "EUR"],
  ["BA", "EUR"],
  ["BE", "EUR"],
  ["BG", "EUR"],
  ["BR", "BRL"],
  ["CA", "CAD"],
  ["CH", "EUR"],
  ["CN", "CNY"],
  ["CZ", "EUR"],
  ["DE", "EUR"],
  ["DK", "EUR"],
  ["EE", "EUR"],
  ["ES", "EUR"],
  ["FI", "EUR"],
  ["FR", "EUR"],
  ["GB", "GBP"],
  ["HR", "EUR"],
  ["HU", "EUR"],
  ["IE", "EUR"],
  ["IN", "INR"],
  ["IT", "EUR"],
  ["JP", "USD"],
  ["LT", "EUR"],
  ["LV", "EUR"],
  ["MX", "MXN"],
  ["NL", "EUR"],
  ["NO", "NOK"],
  ["NZ", "NZD"],
  ["PL", "EUR"],
  ["PT", "EUR"],
  ["RO", "EUR"],
  ["SE", "SEK"],
  ["SI", "EUR"],
  ["SK", "EUR"],
  ["US", "USD"],
  ["ZA", "ZAR"],
]);

export const countryCurrencyFormatterMap = new Map([
  ["AT", "de-AT"],
  ["AU", "en-AU"],
  ["BA", "bs-BA"],
  ["BE", "fr-BE"],
  ["BG", "bg-BG"],
  ["BR", "pt-BR"],
  ["CH", "de-CH"],
  ["CZ", "cs-CZ"],
  ["DE", "de-DE"],
  ["DK", "da-DK"],
  ["EE", "et-EE"],
  ["ES", "es-ES"],
  ["FI", "fi-FI"],
  ["FR", "fr-FR"],
  ["GB", "en-GB"],
  ["HR", "hr-HR"],
  ["HU", "hu-HU"],
  ["IE", "en-IE"],
  ["IN", "en-IN"],
  ["IT", "it-IT"],
  ["LT", "lt-LT"],
  ["LV", "lv-LV"],
  ["NL", "nl-NL"],
  ["NO", "nb-NO"],
  ["NZ", "en-NZ"],
  ["PL", "pl-PL"],
  ["PT", "pt-PT"],
  ["RO", "ro-RO"],
  ["SE", "sv-SE"],
  ["SI", "sl-SI"],
  ["SK", "sk-SK"],
]);

export const countryCurrencySymbolMap = new Map([
  ["AR", "$"],
  ["AT", "€"],
  ["AU", "AU$"],
  ["AX", "€"],
  ["BA", "€"],
  ["BE", "€"],
  ["BG", "€"],
  ["BR", "R$"],
  ["CA", "$"],
  ["CH", "€"],
  ["CN", "¥"],
  ["CZ", "€"],
  ["DE", "€"],
  ["DK", "€"],
  ["EE", "€"],
  ["ES", "€"],
  ["FI", "€"],
  ["FR", "€"],
  ["GB", "£"],
  ["HR", "€"],
  ["HU", "€"],
  ["IE", "€"],
  ["IN", "₹"],
  ["IT", "€"],
  ["JP", "$"],
  ["LT", "€"],
  ["LV", "€"],
  ["MX", "MX$"],
  ["NL", "€"],
  ["NO", "kr"],
  ["NZ", "NZ$"],
  ["PL", "€"],
  ["PT", "€"],
  ["RO", "€"],
  ["SE", "kr"],
  ["SI", "€"],
  ["SK", "€"],
  ["US", "$"],
  ["ZA", "R"],
]);

/**
 * Sales Org-level currency overrides keyed as "COUNTRY-SALESORG".
 * These take precedence over country-level maps only for the specific country+salesOrg pair.
 * Config: MX/2800 → USD, MX/3000 → MXN (country default, no override needed).
 */
export const salesOrgCurrencyMap = new Map<string, string>([
  ["MX-2800", "USD"],
]);

export const salesOrgCurrencySymbolMap = new Map<string, string>([
  ["MX-2800", "$"],
]);

export const urlParamsToOrderTypeMapping: { [k: string]: string } = {
  emergency: "EMERGENCY_ORDER",
  stock: "STOCK_ORDER",
  promotion: "PROMOTION_ORDER",
};

export const orderTypeToUrlParamMapping: { [k: string]: string } = {
  EMERGENCY_ORDER: "emergency",
  STOCK_ORDER: "stock",
  PROMOTION_ORDER: "promotion",
};

export const orderTypeToSAPOrderTypeMapping: { [k: string]: string } = {
  emergency: "ZPIR",
  stock: "ZPIS",
  promotion: "ZPFS",
};

export const getSapOrderType = (
  orderType?: string | null
): string | undefined => {
  if (!orderType) return undefined;

  const urlKey = orderTypeToUrlParamMapping[orderType] ?? orderType;

  return orderTypeToSAPOrderTypeMapping[urlKey];
};

export type PartResponse = {
  partNumber: string;
  description: string;
  hazardousIndicator: boolean;
  criticalCode: string;
  commodityCode: string;
  harmonizedCode: string;
  termCode: string;
  vendorDirectPart: boolean;
  returnable: boolean;
  returnIndicator: string;
  groupingCode: string;
  notes: PartNote[];
  accountId: string;
  package: PartPackageResponse;
  salesOrg: string;
  plantId: string;
  pcrGrp5: string;
  distributionChannel: DistributionChannel;
  itemCategoryGroup: string;
  obsolete: boolean;
  notEffective: boolean;
  notServiceable: boolean;
  heldForReview: boolean;
  invalid: boolean;
  inAccountSalesContract: boolean;
  missingPackageSpecifications: boolean;
  zone: string;
  flashCode: string;
};

export type PartNote = {
  inventoryLocation: string;
  warehouse: string;
  notes: string;
};

export type PartPackageResponse = {
  volume: string;
  volumeUnit: string;
  weight: string;
  weightUnit: string;
  hazardCode: string;
  quantity: string;
  dimensions: PartPackageDimensions;
};

export type DistributionChannel = {
  number: string;
  valid: string;
  status: string;
};

export type OrderLineCancelBody = {
  orderNumber: string;
  requestedBy: string;
  lines: OrderLineCancel[] | [{}];
};

export type OrderLineRouteToDealerBody = {
  orderNumber: string;
  requestedBy: string;
  sellerAccountId: string;
  lines: OrderLineCancel[] | [{}];
};

export type OrderLineCancel = {
  lineNumber: string;
  partNumber: string;
  quantity: number;
};

export const VENDOR_DIRECT_SHIP_UNITS = [
  "331286",
  "352160",
  "355722",
  "356961",
  "361618",
  "317927",
  "329706",
  "331776",
  "334301",
  "337691",
  "347070",
  "355466",
  "357116",
  "369292",
  "369696",
  "369946",
  "377170",
  "4158",
  "9978",
  "16487",
  "88148",
  "311053",
  "331286",
  "338123",
  "347143",
  "352160",
  "355722",
  "356961",
  "358518",
  "361618",
  "372646",
  "376969",
  "5002885",
  "6000020",
  "6000021",
  "6000022",
  "6000023",
  "6000024",
  "6000025",
  "6000026",
  "6000027",
  "6000028",
  "6000029",
  "6000030",
  "6000031",
  "6000032",
  "6000033",
  "6000034",
  "6000035",
  "6000036",
  "6000037",
  "6000038",
  "6000039",
  "6000140",
  "6000141",
  "6000142",
  "6000143",
  "9958",
  "36428",
  "36856",
  "36975",
  "74386",
  "78960",
  "302242",
  "305260",
  "315067",
  "317927",
  "323062",
  "328707",
  "329706",
  "330486",
  "331776",
  "334142",
  "334301",
  "334542",
  "337691",
  "347070",
  "348972",
  "353167",
  "354117",
  "355466",
  "356910",
  "357116",
  "358577",
  "359517",
  "362302",
  "363017",
  "364952",
  "366558",
  "366975",
  "368017",
  "369292",
  "369696",
  "369946",
  "370146",
  "370835",
  "371203",
  "374522",
  "375682",
  "376023",
  "376910",
  "376912",
  "377170",
  "377730",
  "377983",
  "378631",
  "380120",
  "6311236",
  "6363901",
];

export type SummaryState = {
  order: Order;
  orderLineItems: OrderLineItem[];
  promotions: LinePromotion[];
  dealerAccountId: string;
  isLoading: boolean;
  promotionsLoading: boolean;
  apiError: AxiosError | undefined;
  isDownloading: boolean;
  slaComputed: boolean;
  // -- LineSummary
  showPricePopup: PriceModalProps | null;
  showCancelLinesModal: boolean;
  linesToCancel: OrderLineItem[];
  showRouteToDealerModal: boolean;
  linesForRouteToDealer: OrderLineItem[];
  loadingWithOverlay: boolean;
  selectedLinesCount: number;
  sortModel: GridSortModel;
  filters: LineSummaryFilter;
  filteredOrderLineItems: OrderLineItem[] | [];
  paginatedOrderLineItems: OrderLineItem[] | [];
  isFilterApplied: boolean;
  currentPage: number;
  pageRowSize: number;
  computedServiceLevelLines: LineServiceLevelSummary[];
};

export type ReviewLinesState = {
  order: Order;
  orderLineItems: OrderLineItem[];
  filters: LineSummaryFilter;
  filteredOrderLineItems: OrderLineItem[] | [];
  paginatedOrderLineItems: OrderLineItem[] | [];
  isFilterApplied: boolean;
  currentPage: number;
  pageRowSize: number;
  sortModel: GridSortModel;
};

export interface ReviewLinesPayload {
  lines: Line[];
  orderValue?: number | null;
  currencyCode?: string | null;
}

export type LineStat = {
  lineStatus: LineStatus;
  count: number;
};

export type DealerAccountStat = {
  accountId: string;
  lineStats: LineStat[];
};

export type OrderLineStatsResponse = {
  stats: DealerAccountStat[];
};
